import type { PatientResponse } from "../../../shared/types/patient";
import {
  bloodTypeLabel,
  formatDate,
  formatDateTime,
  formatPhone,
  guardianRelationshipLabel,
} from "../../../shared/format";
import { useQuery } from "@tanstack/react-query";
import { useGeoNames } from "../../../shared/api/geo";
import { hisIntegrationApi, hisIntegrationQueryKeys } from "../../../shared/api/hisIntegration";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export function OverviewPanel({ patient }: { patient: PatientResponse }) {
  const { provinceName, wardName } = useGeoNames();
  // HIS→Addy Geo cross-walk: a patient bound to an external HIS stores that HIS's own province/ward
  // ids. Fall back through this map so the address still resolves to a canonical Geo name even after
  // the HIS is disconnected (ADR-0020). Empty for a NATIVE tenant → no effect.
  const hisMap = useQuery({
    queryKey: hisIntegrationQueryKeys.locationMap(),
    queryFn: () => hisIntegrationApi.locationMap(),
    staleTime: 5 * 60 * 1000,
  });
  const hisAlias = new Map((hisMap.data ?? []).map((a) => [String(a.hisCode), a.geoName]));
  const resolve = (code: string | null | undefined, geo: Map<string, string>): string =>
    !code ? "—" : geo.get(String(code)) ?? hisAlias.get(String(code)) ?? code;
  const wardLabel = resolve(patient.wardCode, wardName);
  const provinceLabel = resolve(patient.provinceCode, provinceName);
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Thông tin liên hệ</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            <KV label="Số điện thoại" value={formatPhone(patient.phoneE164)} />
            <KV label="Email" value={patient.email ?? "—"} />
            <KV label="Địa chỉ" value={patient.addressLine ?? "—"} />
            <KV label="Phường/Xã" value={wardLabel} />
            <KV label="Tỉnh/Thành phố" value={provinceLabel} />
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Nhân khẩu học</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            <KV label="Quốc tịch" value={patient.nationality} />
            <KV label="Nghề nghiệp" value={patient.occupation ?? "—"} />
            <KV label="Đơn vị công tác" value={patient.organization ?? "—"} />
            <KV
              label="Nhóm máu"
              value={patient.bloodType ? bloodTypeLabel[patient.bloodType] : "—"}
            />
            <KV
              label="Chiều cao"
              value={patient.heightCm ? `${patient.heightCm} cm` : "—"}
            />
            <KV
              label="Cân nặng"
              value={patient.weightKg ? `${patient.weightKg} kg` : "—"}
            />
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Người giám hộ / Liên hệ khẩn cấp</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            <KV label="Họ tên" value={patient.guardianName ?? "—"} />
            <KV label="Số điện thoại" value={formatPhone(patient.guardianPhone)} />
            <KV
              label="Mối quan hệ"
              value={
                patient.guardianRelationship
                  ? guardianRelationshipLabel[patient.guardianRelationship]
                  : "—"
              }
            />
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ghi chú y khoa</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="whitespace-pre-wrap text-sm text-foreground">
            {patient.notes && patient.notes.trim() !== "" ? patient.notes : "Chưa có ghi chú."}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Hệ thống</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            <KV label="Tạo lúc" value={formatDateTime(patient.createdAt)} />
            <KV label="Cập nhật lúc" value={formatDateTime(patient.updatedAt)} />
            <KV label="Phiên bản" value={String(patient.version)} />
            {patient.deceasedAt && (
              <KV label="Qua đời ngày" value={formatDate(patient.deceasedAt)} />
            )}
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{value}</dd>
    </div>
  );
}
